// Small accessible building blocks on top of React Aria Components, with the website's tokens.

import { ChevronDownIcon, QuestionMarkCircledIcon } from '@radix-ui/react-icons';
import { type ReactNode, type Ref, useEffect, useRef, useState } from 'react';
import {
  Button as AriaButton,
  type ButtonProps,
  Dialog,
  DialogTrigger,
  FieldError,
  Heading,
  Input,
  Label,
  Modal,
  ModalOverlay,
  Popover,
  Radio,
  RadioGroup,
  Text,
  TextArea,
  TextField,
  type TextFieldProps,
} from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { Logo } from './logo';
import { PASSWORD_LIMITS as LIMITS, unmetRules } from './password-rules';
import { publicPage } from './util';

/** Tabler outline icons (24×24, stroke 2), same weight as the public site sprite. */
const ICON_CLASS = 'size-5 shrink-0';

function EyeIcon({ closed }: { closed: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={ICON_CLASS}
      aria-hidden="true"
    >
      {closed ? (
        <>
          <path d="M21 9c-2.4 2.667-5.4 4-9 4c-3.6 0-6.6-1.333-9-4" />
          <path d="M3 15l2.5-3.8" />
          <path d="M21 14.976l-2.492-3.776" />
          <path d="M9 17l.5-4" />
          <path d="M15 17l-.5-4" />
        </>
      ) : (
        <>
          <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0-4 0" />
          <path d="M21 12c-2.4 4-5.4 6-9 6c-3.6 0-6.6-2-9-6c2.4-4 5.4-6 9-6c3.6 0 6.6 2 9 6" />
        </>
      )}
    </svg>
  );
}

type FieldProps = Omit<TextFieldProps, 'children'> & {
  label: string;
  description?: string;
  /** A text area for longer answers (a justification, a comment). */
  multiline?: boolean;
  /** The label stays for assistive technology only; the placeholder is what sighted users see. */
  hideLabel?: boolean;
  placeholder?: string;
  /** An error from outside the browser's own validation (e.g. an address the server says is not valid). */
  error?: string | null;
  /** Draws an asterisk after the label of a required field (the page explains it with RequiredNote). */
  markRequired?: boolean;
};

/** What a field says when the browser's own validation rejects it. */
function FieldErrors({
  type,
  minLength,
  message,
}: {
  type?: string;
  minLength?: number;
  /** An error that comes from somewhere else (the server's answer), shown instead of the browser's. */
  message?: string | null;
}) {
  const { t } = useTranslation();
  if (message) return <FieldError className="text-sm font-bold text-error">{message}</FieldError>;
  return (
    <FieldError className="text-sm font-bold text-error">
      {({ validationDetails, validationErrors }) =>
        validationDetails.valueMissing
          ? t('errors.required')
          : validationDetails.typeMismatch
            ? t('forms.email.invalid')
            : validationDetails.tooShort
              ? t('errors.tooShort', { min: minLength })
              : validationErrors.join(' ')
      }
    </FieldError>
  );
}

const INPUT_CLASS =
  'w-full min-h-11 rounded-md border border-field-border bg-paper px-3 py-2 text-ink placeholder:text-ink-muted data-[invalid]:border-error';

/**
 * When a field says what is wrong with it: once it has been left after being started, or once the form was
 * sent with it wrong (never when the page loads and never from the first character), and no longer as soon
 * as the value is right. `check` says what is wrong with the value now, or null.
 */
function useLocalErrors(check: (value: string, input: HTMLInputElement | null) => string | null) {
  const input = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const [started, setStarted] = useState(false);
  const [left, setLeft] = useState(false);
  const [sent, setSent] = useState(false);
  const revealed = left || sent;
  const current = check(value, input.current);
  return {
    input,
    value,
    revealed,
    /** What the field says now: the problem with its value, if it is time to say it. */
    shown: revealed ? current : null,
    handlers: {
      onChange: (next: string) => {
        setValue(next);
        setStarted(true);
      },
      onBlur: () => {
        if (started) setLeft(true);
      },
      // The browser found the field wrong when the form was sent.
      onInvalid: () => setSent(true),
    },
  };
}

export function Field({
  label,
  description,
  multiline = false,
  hideLabel = false,
  placeholder,
  error,
  markRequired = false,
  ...props
}: FieldProps) {
  const { t } = useTranslation();
  // An email is checked as the sign-in screens ask: what it says is in form-messages.ts.
  const email = props.type === 'email' && !multiline;
  const local = useLocalErrors((value, input) =>
    !value
      ? props.isRequired
        ? t('forms.email.required')
        : null
      : input?.validity.typeMismatch
        ? t('forms.email.invalid')
        : null,
  );
  const message = error ?? (email ? local.shown : null);
  return (
    <TextField
      {...props}
      {...(email
        ? {
            onChange: (next: string) => {
              local.handlers.onChange(next);
              props.onChange?.(next);
            },
            onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
              local.handlers.onBlur();
              props.onBlur?.(event);
            },
          }
        : {})}
      isInvalid={email ? Boolean(message) : error ? true : props.isInvalid}
      className="flex flex-col gap-2"
    >
      <Label className={hideLabel ? 'sr-only' : 'font-bold'}>
        {label}
        {/* The field itself is `required` (screen readers announce it); the asterisk is only the visual mark. */}
        {markRequired && props.isRequired && <span aria-hidden="true"> *</span>}
      </Label>
      {description && (
        <Text slot="description" className="text-sm text-ink-muted">
          {description}
        </Text>
      )}
      {multiline ? (
        <TextArea rows={3} placeholder={placeholder} className={INPUT_CLASS} />
      ) : (
        <Input
          ref={email ? local.input : undefined}
          onInvalid={email ? local.handlers.onInvalid : undefined}
          placeholder={placeholder}
          className={INPUT_CLASS}
        />
      )}
      {email ? (
        <FieldError className="text-sm font-bold text-error">{message}</FieldError>
      ) : (
        <FieldErrors type={props.type} minLength={props.minLength} message={error} />
      )}
    </TextField>
  );
}

/** What the asterisk after a label means; it goes before the form, where it is read before the fields. */
export function RequiredNote() {
  const { t } = useTranslation();
  return (
    <p id="required-note" className="text-sm text-ink-muted">
      {t('form.requiredNote')}
    </p>
  );
}

/**
 * The one password field of the three screens. It starts hidden and can be shown and hidden again with an
 * eye icon inside the input (the value stays and nothing is sent); paste and the browser's or a
 * password manager's autofill work (`new-password` when one is chosen, `current-password` when it is
 * entered). When it is chosen (`mode="new"`) the minimum length comes under the input and is marked
 * once the field is left or the form is sent, never from the first character; the maximum only appears
 * there when that limit is broken. When it is entered (`mode="current"`) there are no requirements and
 * no length checks, because an existing password only has to be right.
 */
export function PasswordField({
  label,
  mode,
  error,
  ...props
}: Omit<TextFieldProps, 'children' | 'type' | 'autoComplete' | 'minLength' | 'maxLength' | 'validate'> & {
  label: string;
  mode: 'new' | 'current';
  /** An error from the server's answer (a password it did not accept). */
  error?: string | null;
}) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const choosing = mode === 'new';
  // A new password says which rule it breaks (an empty one too); an existing one only has to be there.
  const local = useLocalErrors((current) => {
    if (choosing) {
      const first = unmetRules(current)[0];
      return first ? t(`password.errors.${first}`, LIMITS) : null;
    }
    return current ? null : t('forms.password.required');
  });
  const { value } = local;
  const unmet = unmetRules(value);
  // An answer of the server about the value is about that value: once it is edited it no longer holds (and an
  // invalid field would keep the form from being sent again).
  const [edited, setEdited] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new answer is a new error, whatever the last edit.
  useEffect(() => setEdited(false), [error]);
  const message = (edited ? null : error) ?? local.shown;
  return (
    <TextField
      {...props}
      type={visible ? 'text' : 'password'}
      autoComplete={choosing ? 'new-password' : 'current-password'}
      onChange={(next) => {
        setEdited(true);
        local.handlers.onChange(next);
        props.onChange?.(next);
      }}
      onBlur={(event) => {
        local.handlers.onBlur();
        props.onBlur?.(event);
      }}
      // The same rules the screen lists, run by the browser's own validation so the form does not send and
      // the first field in error takes the focus; the value can be set by a password manager.
      validate={
        choosing ? (next) => unmetRules(next).map((rule) => t(`password.errors.${rule}`, LIMITS)) : undefined
      }
      isInvalid={Boolean(message)}
      className="flex flex-col gap-2"
    >
      <Label className="font-bold">{label}</Label>
      <div className="relative w-full">
        <Input
          ref={local.input}
          onInvalid={local.handlers.onInvalid}
          className={`${INPUT_CLASS} pr-11`}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
        />
        <AriaButton
          aria-label={t(visible ? 'password.hide' : 'password.show')}
          onPress={() => {
            setVisible(!visible);
            setAnnouncement(t(visible ? 'password.nowHidden' : 'password.nowVisible'));
          }}
          className="absolute inset-y-0 right-0 flex min-h-11 min-w-11 items-center justify-center rounded-r-md text-ink-muted hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand"
        >
          {/* Hidden → closed eye; visible → open eye (Tabler eye-closed / eye). */}
          <EyeIcon closed={!visible} />
        </AriaButton>
      </div>
      {choosing && (
        <Text slot="description" elementType="div" className="password-rules">
          <ul>
            {(
              [
                'min',
                // The max is a hard limit, not a target: list it only when that error is active.
                ...(local.revealed && unmet.includes('max') ? (['max'] as const) : []),
              ] as const
            ).map((rule) => {
              const state = local.revealed && value ? (unmet.includes(rule) ? 'unmet' : 'met') : 'idle';
              return (
                <li key={rule} data-state={state}>
                  <span aria-hidden="true" className="rule-mark">
                    {state === 'met' ? '✓' : state === 'unmet' ? '✕' : '•'}
                  </span>
                  {t(`password.rules.${rule}`, LIMITS)}
                  {state !== 'idle' && <span className="sr-only"> ({t(`password.rules.${state}`)})</span>}
                </li>
              );
            })}
          </ul>
        </Text>
      )}
      <FieldError className="text-sm font-bold text-error">{message}</FieldError>
      <span role="status" className="sr-only">
        {announcement}
      </span>
    </TextField>
  );
}

type ButtonVariant = 'primary' | 'secondary' | 'danger';

const BUTTON_LOOK: Record<ButtonVariant, string> = {
  primary: 'bg-brand text-on-brand hover:bg-brand-strong',
  secondary: 'border border-line bg-paper text-ink hover:bg-brand-tint',
  danger: 'bg-error text-on-brand hover:opacity-90',
};

/** Classes of a button, also for links that look like one. */
export function buttonClass(variant: ButtonVariant = 'primary') {
  return `ui-button inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-5 py-2 text-sm font-bold no-underline data-[disabled]:opacity-60 data-[pending]:cursor-progress data-[pending]:opacity-60 ${BUTTON_LOOK[variant]}`;
}

export function Button({
  variant = 'primary',
  className,
  ...props
}: ButtonProps & { variant?: ButtonVariant; className?: string; ref?: Ref<HTMLButtonElement> }) {
  return <AriaButton {...props} className={`${buttonClass(variant)} ${className ?? ''}`} />;
}

/**
 * A button that asks before doing something that cannot be undone (an alertdialog with the focus on
 * "Cancel"). `hidden` completes the button's name for screen readers when the visible label is short.
 */
export function ConfirmButton({
  label,
  hidden,
  title,
  body,
  confirm,
  cancel,
  onConfirm,
  variant = 'danger',
  isDisabled,
}: {
  label: string;
  hidden?: string;
  title: string;
  body: string;
  confirm: string;
  cancel: string;
  onConfirm: () => Promise<void>;
  /** `danger` for what cannot be undone; `primary` for a change that only asks before it is made. */
  variant?: 'danger' | 'primary';
  isDisabled?: boolean;
}) {
  return (
    <DialogTrigger>
      <Button variant={variant} isDisabled={isDisabled}>
        {label}
        {hidden && <span className="sr-only"> {hidden}</span>}
      </Button>
      <ModalOverlay
        isDismissable
        className="fixed inset-0 z-20 flex items-center justify-center bg-black/60 p-4"
      >
        <Modal className="w-full max-w-md rounded-lg border-2 border-line bg-paper p-6 text-ink">
          <Dialog role="alertdialog" className="outline-none">
            {({ close }) => (
              <div className="flex flex-col gap-4">
                <Heading slot="title" className="text-xl font-bold">
                  {title}
                </Heading>
                <p>{body}</p>
                <div className="flex flex-wrap justify-end gap-3">
                  <Button variant="secondary" onPress={close} autoFocus>
                    {cancel}
                  </Button>
                  <Button
                    variant={variant}
                    onPress={async () => {
                      close();
                      await onConfirm();
                    }}
                  >
                    {confirm}
                  </Button>
                </div>
              </div>
            )}
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
}

/**
 * A part of a screen that opens and closes: the whole header is the control, an arrow says so (down closed, up open)
 * and the state is told by the browser itself (a native `details`: keyboard and screen readers work as they are).
 * `summary` is the title; `hint` is a quiet line beside it that says what is inside without opening it.
 */
export function Fold({
  title,
  hint,
  children,
  className = '',
  light = false,
  name,
  onToggle,
}: {
  title: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
  /** A fold inside another one: no box of its own, only a line. */
  light?: boolean;
  /** Same `name` on several folds: only one stays open (native accordion). */
  name?: string;
  onToggle?: (open: boolean) => void;
}) {
  return (
    <details
      className={`fold ${light ? 'fold-light' : ''} ${className}`}
      name={name}
      onToggle={onToggle ? (event) => onToggle((event.currentTarget as HTMLDetailsElement).open) : undefined}
    >
      <summary>
        <span className="fold-title">
          <span className="fold-name">{title}</span>
          {hint && <span className="fold-hint">{hint}</span>}
        </span>
        <ChevronDownIcon className="fold-arrow" aria-hidden="true" />
      </summary>
      <div className="fold-body">{children}</div>
    </details>
  );
}

/** One choice among a few, each with a short explanation (radio buttons drawn as cards). */
export function Choice<T extends string>({
  label,
  description,
  options,
  hideLabel = false,
  ...props
}: {
  label: string;
  description?: string;
  name?: string;
  /** When the heading already names the group (e.g. a wizard step title). */
  hideLabel?: boolean;
  /** `action` sits inside the option's card but outside its radio, so a link or button there is not part of the label. */
  options: { value: T; label: string; hint?: string; disabled?: boolean; action?: ReactNode }[];
  /** `null`: controlled, with nothing chosen yet. */
  value?: T | null;
  defaultValue?: T;
  onChange?: (value: T) => void;
  /** False when nothing has to be chosen yet (a setting that starts undefined). */
  isRequired?: boolean;
}) {
  const { isRequired = true, ...rest } = props;
  return (
    <RadioGroup
      {...rest}
      onChange={rest.onChange as ((value: string) => void) | undefined}
      isRequired={isRequired}
      className="flex flex-col gap-2"
    >
      <Label className={hideLabel ? 'sr-only' : 'font-bold'}>{label}</Label>
      {description && (
        <Text slot="description" className="text-sm text-ink-muted">
          {description}
        </Text>
      )}
      {options.map((option) => (
        <div
          key={option.value}
          className="rounded-lg border border-line bg-paper has-[[data-selected]]:border-brand has-[[data-selected]]:bg-brand-tint has-[[data-focus-visible]]:outline-3 has-[[data-focus-visible]]:outline-offset-2 has-[[data-focus-visible]]:outline-brand"
        >
          <Radio
            value={option.value}
            isDisabled={option.disabled}
            className="group flex cursor-pointer gap-3 p-4 outline-none data-[disabled]:cursor-not-allowed"
          >
            <span
              aria-hidden="true"
              className="mt-0.5 size-5 shrink-0 rounded-full border border-field-border group-data-[selected]:border-[6px] group-data-[selected]:border-brand group-data-[disabled]:border-line"
            />
            <span className="flex flex-col">
              <span className="font-bold">{option.label}</span>
              {option.hint && <span className="text-sm text-ink-muted">{option.hint}</span>}
            </span>
          </Radio>
          {option.action && <div className="px-4 pb-4 pl-12">{option.action}</div>}
        </div>
      ))}
    </RadioGroup>
  );
}

/** Errors are announced as they appear; confirmations are polite. */
/** A link to the public website that opens in a new tab, so nothing typed or chosen here is lost. */
export function WebsiteLink({ href, children }: { href: string; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
      <span className="sr-only"> ({t('app.newTab')})</span>
    </a>
  );
}

export function Message({ tone, children }: { tone: 'error' | 'ok'; children: ReactNode }) {
  return tone === 'error' ? (
    <p role="alert" className="rounded-md border border-error bg-error-tint p-3 font-bold text-error">
      {children}
    </p>
  ) : (
    <p role="status" className="rounded-md border border-ok bg-ok-tint p-3 font-bold text-ok">
      {children}
    </p>
  );
}

/**
 * Says something to people who use a screen reader, whatever happens to the screen that triggered it (the
 * card that showed a confirmation may move to another list once the change is saved). The text is also
 * shown where the person acted; this is the one that is not lost.
 */
export function announce(message: string) {
  if (typeof document === 'undefined') return;
  let region = document.getElementById('app-announcer');
  if (!region) {
    region = document.createElement('div');
    region.id = 'app-announcer';
    region.setAttribute('role', 'status');
    region.className = 'sr-only';
    document.body.append(region);
  }
  // The same sentence twice in a row is read again only if the region changes.
  region.textContent = '';
  setTimeout(() => {
    if (region) region.textContent = message;
  }, 50);
}

let navigated = false;

/** Called once with the router: after the first client-side navigation, new pages take the focus. */
export function followNavigations(router: {
  subscribe: (
    type: 'onBeforeNavigate',
    fn: (event: { fromLocation?: unknown; pathChanged: boolean }) => void,
  ) => unknown;
}) {
  router.subscribe('onBeforeNavigate', (event) => {
    if (event.fromLocation && event.pathChanged) navigated = true;
  });
}

let focusRequestedAt = 0;

/**
 * A control that stays on the page while it leads to other content (a button in the head of a website's
 * page) asks for the heading of that content to take the focus, as `useArrivalFocus` does by itself when
 * the link that led there disappears.
 */
export function requestContentFocus() {
  focusRequestedAt = Date.now();
}

/**
 * The heading of content that replaces other content inside the same page (a tab of a website). After
 * a client-side navigation it takes the focus when the focus was lost (the link that led here is gone)
 * or a control asked for it, so that screen readers announce it. Not when the tab bar itself led here
 * (the focus stays on it), and not on the first load. `ready` says the heading is already on screen.
 */
export function useArrivalFocus<T extends HTMLElement = HTMLHeadingElement>(ready = true) {
  const ref = useRef<T>(null);
  const done = useRef(false);
  useEffect(() => {
    if (!ready || done.current) return;
    done.current = true;
    const lost = !document.activeElement || document.activeElement === document.body;
    const asked = Date.now() - focusRequestedAt < 5000;
    if (navigated && (lost || asked)) ref.current?.focus();
    focusRequestedAt = 0;
  }, [ready]);
  return ref;
}

/** The document title from its parts (the section, the website…); the product name comes last. */
export function usePageTitle(...parts: (string | undefined)[]) {
  const title = parts.filter(Boolean).join(' | ');
  useEffect(() => {
    if (title) document.title = `${title} | SiteNorma`;
  }, [title]);
}

/**
 * A page with its <h1>: sets the document title and moves the focus to the heading when another page
 * (or another step of the same page) replaces it, so screen readers announce it. Not on the first
 * load, and not when only the language changes. Pages whose title is data (a site's name, which can
 * be edited in place) pass a stable `focusKey` so that saving does not move the focus.
 */
export function Page({
  title,
  titleExtra,
  children,
  wide = false,
  fluid = false,
  focusKey = title,
  setsDocumentTitle = true,
  brand = false,
}: {
  title: string;
  /** Something that belongs to the title and sits right next to it (a status). */
  titleExtra?: ReactNode;
  children: ReactNode;
  wide?: boolean;
  /** Takes all the width there is (on a desktop), with no maximum: for a website's own pages. */
  fluid?: boolean;
  focusKey?: string;
  /** False when what is inside names the document (a section of the website), with `usePageTitle`. */
  setsDocumentTitle?: boolean;
  /** Logo above the title that links to the public website (sign-in and related screens). */
  brand?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const heading = useRef<HTMLHeadingElement>(null);
  const shown = useRef<{ key: string; lang: string } | null>(null);
  useEffect(() => {
    if (setsDocumentTitle) document.title = `${title} | SiteNorma`;
  }, [title, setsDocumentTitle]);
  useEffect(() => {
    const previous = shown.current;
    const replaced = previous ? previous.lang === i18n.language && previous.key !== focusKey : navigated;
    if (replaced) heading.current?.focus();
    shown.current = { key: focusKey, lang: i18n.language };
  }, [focusKey, i18n.language]);
  return (
    <div className={`page ${fluid ? 'page-fluid' : wide ? 'page-wide' : 'page-form'}`}>
      {brand && (
        <a href={publicPage(i18n.language, 'home')} aria-label={t('app.website')} className="auth-brand">
          <Logo />
        </a>
      )}
      {titleExtra ? (
        <div className="page-heading">
          <h1 ref={heading} tabIndex={-1} className="page-title">
            {title}
          </h1>
          {titleExtra}
        </div>
      ) : (
        <h1 ref={heading} tabIndex={-1} className="page-title">
          {title}
        </h1>
      )}
      {children}
    </div>
  );
}

/**
 * A contextual help: a small button next to what it explains that opens a short text beside it (a popover
 * that opens with a press or the keyboard, closes with Escape or a press outside, and gives the focus back).
 * `label` names the button for assistive technology (what the help is about).
 */
export function HelpPopover({ label, children }: { label: string; children: ReactNode }) {
  return (
    <DialogTrigger>
      <AriaButton aria-label={label} className="help-button">
        <QuestionMarkCircledIcon aria-hidden="true" />
      </AriaButton>
      <Popover placement="bottom start" className="help-popover">
        <Dialog aria-label={label} className="help-dialog">
          {children}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}
